import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Clock, Mail, Phone, MapPin, Calendar, Sparkles, ChevronLeft, ChevronRight, X } from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Flatten gallery into a single array of images for lightbox navigation
  const lightboxImages = gallery.flatMap((item) => [
    { src: item.before_image, title: item.title, label: 'Antes' },
    { src: item.after_image, title: item.title, label: 'Después' },
  ]);

  const openLightbox = (itemIndex, type) => {
    // type: 'before' (0) or 'after' (1) within that item
    const offset = type === 'before' ? 0 : 1;
    setLightboxIndex(itemIndex * 2 + offset);
  };

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  const prevImage = useCallback(() => {
    setLightboxIndex((i) => (i === null ? i : (i - 1 + lightboxImages.length) % lightboxImages.length));
  }, [lightboxImages.length]);

  const nextImage = useCallback(() => {
    setLightboxIndex((i) => (i === null ? i : (i + 1) % lightboxImages.length));
  }, [lightboxImages.length]);

  // Keyboard navigation
  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex, closeLightbox, prevImage, nextImage]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [servicesRes, galleryRes] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/gallery`)
      ]);
      setServices(servicesRes.data);
      setGallery(galleryRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const formatDuration = (minutes) => {
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
    }
    return `${minutes} min`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-pink-100">
      {/* Hero Section */}
      <section className="relative overflow-hidden min-h-[700px] flex items-center">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_4de03272-c81e-421b-bd6b-8314832fa602/artifacts/jn61riuo_maria%20los%20angeles.jpeg)'
          }}
        >
          {/* Gradient overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60"></div>
        </div>
        
        {/* Content */}
        <div className="relative container mx-auto px-4 py-20 z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 mb-6 px-6 py-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg">
              <Sparkles className="w-5 h-5 text-pink-500" />
              <span className="text-sm font-medium text-pink-600">Tu belleza, nuestra pasión</span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight drop-shadow-2xl">
              Los Angeles Royale Beauty
            </h1>
            <p className="text-lg sm:text-xl text-white/95 mb-8 max-w-2xl mx-auto drop-shadow-lg">
              Centro de belleza especializado en tratamientos faciales, manicure, depilación y maquillaje profesional en Miami Dade
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button 
                onClick={() => navigate('/reservar')} 
                size="lg"
                className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white px-8 py-6 text-lg rounded-full shadow-xl"
                data-testid="reservar-cita-btn"
              >
                <Calendar className="mr-2 h-5 w-5" />
                Reservar Cita
              </Button>
              <Button 
                onClick={() => navigate('/admin/login')} 
                variant="outline"
                size="lg"
                className="bg-white/90 backdrop-blur-sm border-2 border-white hover:bg-white px-8 py-6 text-lg rounded-full text-gray-800"
                data-testid="admin-login-btn"
              >
                Panel Admin
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 px-4" data-testid="services-section">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-800 mb-4">Nuestros Servicios</h2>
            <p className="text-lg text-gray-600">Descubre la experiencia que tenemos para ti</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Card 
                key={service.id} 
                className="group hover:shadow-2xl transition-all duration-300 border-2 border-pink-100 hover:border-pink-300 bg-white/90 backdrop-blur-sm"
                data-testid={`service-card-${index}`}
              >
                <CardHeader>
                  <div className="w-16 h-16 bg-gradient-to-br from-pink-400 to-rose-400 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <span className="text-3xl font-bold text-white">{index + 1}</span>
                  </div>
                  <CardTitle className="text-xl mb-2">{service.name}</CardTitle>
                  <CardDescription className="text-base">{service.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center text-pink-600 font-semibold">
                    <Clock className="mr-2 h-5 w-5" />
                    <span data-testid={`service-duration-${index}`}>{formatDuration(service.duration_minutes)}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      {gallery.length > 0 && (
        <section className="py-16 px-4 bg-white/50" data-testid="gallery-section">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-800 mb-4">Antes y Después</h2>
              <p className="text-lg text-gray-600">Resultados que hablan por sí mismos</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {gallery.map((item, index) => (
                <div key={item.id} className="group" data-testid={`gallery-item-${index}`}>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">{item.title}</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => openLightbox(index, 'before')}
                      className="relative overflow-hidden rounded-2xl shadow-lg cursor-zoom-in focus:outline-none focus:ring-4 focus:ring-pink-300"
                      data-testid={`gallery-before-btn-${index}`}
                    >
                      <div className="absolute top-2 left-2 bg-gray-800/80 text-white px-3 py-1 rounded-full text-sm font-medium z-10">
                        Antes
                      </div>
                      <img 
                        src={item.before_image} 
                        alt="Antes" 
                        className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                        data-testid={`gallery-before-${index}`}
                      />
                    </button>
                    <button
                      type="button"
                      onClick={() => openLightbox(index, 'after')}
                      className="relative overflow-hidden rounded-2xl shadow-lg cursor-zoom-in focus:outline-none focus:ring-4 focus:ring-pink-300"
                      data-testid={`gallery-after-btn-${index}`}
                    >
                      <div className="absolute top-2 left-2 bg-pink-600 text-white px-3 py-1 rounded-full text-sm font-medium z-10">
                        Después
                      </div>
                      <img 
                        src={item.after_image} 
                        alt="Después" 
                        className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                        data-testid={`gallery-after-${index}`}
                      />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox Modal */}
      {lightboxIndex !== null && lightboxImages[lightboxIndex] && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={closeLightbox}
          data-testid="lightbox-overlay"
        >
          {/* Close */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            className="absolute top-4 right-4 text-white hover:text-pink-300 transition-colors p-2 rounded-full bg-white/10 hover:bg-white/20"
            aria-label="Cerrar"
            data-testid="lightbox-close-btn"
          >
            <X className="w-7 h-7" />
          </button>

          {/* Prev */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-4 md:left-8 text-white hover:text-pink-300 transition-colors p-3 rounded-full bg-white/10 hover:bg-white/20"
            aria-label="Anterior"
            data-testid="lightbox-prev-btn"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>

          {/* Image + caption */}
          <div
            className="max-w-5xl max-h-[85vh] flex flex-col items-center gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxImages[lightboxIndex].src}
              alt={lightboxImages[lightboxIndex].label}
              className="max-h-[75vh] max-w-full object-contain rounded-lg shadow-2xl"
              data-testid="lightbox-image"
            />
            <div className="flex items-center gap-3 text-white">
              <span className={`px-4 py-1 rounded-full text-sm font-medium ${lightboxImages[lightboxIndex].label === 'Antes' ? 'bg-gray-700' : 'bg-pink-600'}`}>
                {lightboxImages[lightboxIndex].label}
              </span>
              <span className="text-lg font-semibold">{lightboxImages[lightboxIndex].title}</span>
              <span className="text-sm text-white/70">
                {lightboxIndex + 1} / {lightboxImages.length}
              </span>
            </div>
          </div>

          {/* Next */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-4 md:right-8 text-white hover:text-pink-300 transition-colors p-3 rounded-full bg-white/10 hover:bg-white/20"
            aria-label="Siguiente"
            data-testid="lightbox-next-btn"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
        </div>
      )}

      {/* Contact Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <Card className="bg-gradient-to-br from-pink-500 to-rose-500 text-white border-0 shadow-2xl">
            <CardContent className="p-12">
              <h2 className="text-4xl font-bold mb-8 text-center">Contáctanos</h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="flex items-start gap-4">
                  <Phone className="w-6 h-6 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Teléfono</h3>
                    <p className="text-pink-50">+1-786-813-8531</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Mail className="w-6 h-6 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Email</h3>
                    <p className="text-pink-50">mdlaiv1990@gmail.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 md:col-span-2">
                  <MapPin className="w-6 h-6 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Horario</h3>
                    <p className="text-pink-50">Lunes - Sábado: 9:00 AM - 7:00 PM (ET)</p>
                    <p className="text-pink-50">Domingo: Cerrado</p>
                    <p className="text-pink-50 text-sm mt-1">Horario de Miami Dade (Eastern Time)</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-lg font-semibold mb-2">Los Angeles Royale Beauty</p>
          <p className="text-gray-400">© 2025 Todos los derechos reservados</p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;